import { windowLabel } from '../status';
import { bookedAt, durationLabel, listOf, timeInTz } from '../../lib/format';
import {
  canceledVisits,
  dayOrder,
  firstName,
  type CrewJobSource,
  type CrewState,
  type CrewTechSource,
} from './crewPins';

/**
 * What the crew map's right panel says (ops insights Phase 4, ticket 04, spec §3): which tab
 * a job is under, the one fact on its row, a picked tech's "now" and the timeline of their
 * day. Pure, with the shop's zone passed in, so every sentence is a test rather than a look.
 *
 * ⚠️ **It reads the server's answer and never re-decides it** (thin client). Where a tech is,
 * which visit they are at or heading to, and which visits they passed over are `crew.day`'s
 * (`crewPlacement.ts`: `state`, `place`, `atVisitId`, `toVisitId`, `skipped`), named even when
 * the map cannot draw them. What this module adds is WORDS.
 *
 * ⚠️ **Times are the shop's clock** (`timeInTz`), and a duration is REAL elapsed time counted
 * between the two MINUTES printed (`durationLabel`).
 *
 * ⚠️ **No GPS, in words, wherever the panel explains an en-route tech** (ADR 0001): the line
 * on the map is drawn between two job addresses, and the panel never lets it pass for a
 * position.
 */

/** A job as the panel reads it: the pins' fields, plus the times, the window and the crew. */
export interface PanelJob extends CrewJobSource {
  visit: CrewJobSource['visit'] & {
    scheduledEndAt: Date | string | null;
    window: string;
    actualStartAt: Date | string | null;
    actualEndAt: Date | string | null;
  };
  job: CrewJobSource['job'] & {
    id: string;
    jobNumber: number;
    serviceType: string | null;
  };
  property: CrewJobSource['property'] & { label: string | null };
  assignees: { userId: string; name: string }[];
}

/* ───────────────────────── Tabs ───────────────────────── */

export type PanelTab = 'all' | 'in_progress' | 'up_next' | 'unassigned' | 'done';

export const PANEL_TABS: readonly { key: PanelTab; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'up_next', label: 'Up next' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'done', label: 'Done' },
];

/**
 * The one tab (besides All) a job is under, or null for a canceled visit.
 *
 * ⭐ **A partition, and every count on screen adds up**: every job that is not canceled is
 * under exactly one of In progress, Up next, Unassigned and Done, and All is those four. A
 * canceled visit is in NO tab count: the All list shows it below, under its own "N canceled"
 * line (review: "All 11" over 10 + 1 hidden canceled did not add up).
 * - Unassigned is work still to do that nobody is on, the header's own "jobs unassigned"
 *   (`crew.ts`'s `countsOf`): not done, not canceled, no assignee. It is taken out of In
 *   progress and Up next, so a job is never counted twice.
 * - In progress holds en route too: someone is on it, the work is under way.
 */
export function tabOf(job: PanelJob): Exclude<PanelTab, 'all'> | null {
  const status = job.visit.status;
  if (status === 'canceled') return null;
  if (status === 'completed') return 'done';
  if (job.assignees.length === 0) return 'unassigned';
  if (status === 'in_progress' || status === 'en_route') return 'in_progress';
  return 'up_next';
}

/** Whether a job's row is in a tab's list. All lists the canceled too, apart, below. */
export function inTab(job: PanelJob, tab: PanelTab): boolean {
  return tab === 'all' || tabOf(job) === tab;
}

export function tabCounts(
  jobs: readonly PanelJob[],
): Record<PanelTab, number> & { canceled: number } {
  const counts = {
    all: 0,
    in_progress: 0,
    up_next: 0,
    unassigned: 0,
    done: 0,
    canceled: 0,
  };
  for (const j of jobs) {
    const tab = tabOf(j);
    if (tab) {
      counts[tab] += 1;
      counts.all += 1;
    } else counts.canceled += 1;
  }
  return counts;
}

/**
 * The day's visits in words, as every count on the crew map reads them: the visits still on
 * (the panel's "All"), and the canceled apart. "11 visits · 1 canceled", "1 visit".
 */
export function visitsLine(jobs: readonly PanelJob[]): string {
  const { all, canceled } = tabCounts(jobs);
  const visits = `${all} ${all === 1 ? 'visit' : 'visits'}`;
  return canceled > 0 ? `${visits} · ${canceled} canceled` : visits;
}

/* ───────────────────────── Times ───────────────────────── */

const instant = (d: Date | string | null): number | null =>
  d === null ? null : new Date(d).getTime();

/** What was booked: "Window 1:00 PM–5:00 PM", "Morning window", "Booked 9:30 AM–10:30 AM". */
function booking(job: PanelJob, tz: string, date: string): string {
  const { scheduledStartAt: start, scheduledEndAt: end, window } = job.visit;
  const named = windowLabel(window);
  if (start === null) return 'Not booked';
  const span = `${bookedAt(start, tz, date)}${end === null ? '' : `–${timeInTz(end, tz)}`}`;
  if (named === null) return `Booked ${span}`;
  return end === null ? `${named} window` : `Window ${span}`;
}

/* ───────────────────────── Who and where ───────────────────────── */

/** "Maya is" / "Maya and Dan are". */
const whoIs = (names: string[]) =>
  `${listOf(names)} ${names.length === 1 ? 'is' : 'are'}`;

/** A tech's own jobs in the day, in their day's order. */
function mine(tech: CrewTechSource, byId: ReadonlyMap<string, PanelJob>): PanelJob[] {
  return tech.visits.flatMap((id) => {
    const j = byId.get(id);
    return j ? [j] : [];
  });
}

const jobOf = (id: string | null, byId: ReadonlyMap<string, PanelJob>) =>
  id === null ? null : (byId.get(id) ?? null);

/**
 * Their latest check-out: the visit they last LEFT, which "Checked out of A at T" names.
 * A fact read off the rows the server sent (the latest `actualEndAt`), not a rule.
 */
function lastLeft(
  tech: CrewTechSource,
  byId: ReadonlyMap<string, PanelJob>,
): PanelJob | null {
  let best: PanelJob | null = null;
  for (const j of mine(tech, byId)) {
    if (j.visit.status !== 'completed') continue;
    const t = instant(j.visit.actualEndAt);
    if (t !== null && (best === null || t > instant(best.visit.actualEndAt)!)) best = j;
  }
  return best;
}

/* ───────────────────────── A row's one fact ───────────────────────── */

/** Every en-route explanation ends with this (ADR 0001). */
export const NO_GPS = 'No GPS';

/**
 * The one fact on an appointment's row, by its visit status:
 * - in progress: "Checked in 9:15 AM";
 * - done: "Checked out 11:02 AM · 1 h 47 min";
 * - en route: "Maya is en route · left Beaumont at 10:42 AM · No GPS";
 * - booked: "Window 1:00 PM–5:00 PM", or "Booked 9:30 AM–10:30 AM";
 * - canceled: "Was booked 2:00 PM".
 */
export function jobFact(
  job: PanelJob,
  techs: readonly CrewTechSource[],
  byId: ReadonlyMap<string, PanelJob>,
  tz: string,
  date: string,
): string {
  const v = job.visit;
  switch (v.status) {
    case 'in_progress':
      return v.actualStartAt === null
        ? 'In progress'
        : `Checked in ${timeInTz(v.actualStartAt, tz)}`;
    case 'completed':
      if (v.actualEndAt === null) return 'Done';
      return v.actualStartAt === null
        ? `Checked out ${timeInTz(v.actualEndAt, tz)}`
        : `Checked out ${timeInTz(v.actualEndAt, tz)} · ${durationLabel(v.actualStartAt, v.actualEndAt)}`;
    case 'en_route':
      return enRouteFact(job, techs, byId, tz);
    case 'canceled':
      return v.scheduledStartAt === null
        ? 'Canceled'
        : `Was booked ${bookedAt(v.scheduledStartAt, tz, date)}`;
    default:
      return booking(job, tz, date);
  }
}

/**
 * Who is on the way to this job, and from where: the tech the server drew on the line to it
 * ("left A at T"), or, when it is not where they are heading first, the job they are [Q1].
 */
function enRouteFact(
  job: PanelJob,
  techs: readonly CrewTechSource[],
  byId: ReadonlyMap<string, PanelJob>,
  tz: string,
): string {
  const crew = new Set(job.assignees.map((a) => a.userId));
  const heading = techs.filter((t) => t.state === 'en_route' && crew.has(t.userId));
  if (heading.length === 0) return `En route · ${NO_GPS}`;
  const who = whoIs(heading.map((t) => firstName(t.name)));
  const drawn = heading.find(
    (t) => t.place?.kind === 'between' && t.place.toVisitId === job.visit.id,
  );
  if (drawn?.place?.kind === 'between') {
    const from =
      byId.get(drawn.place.fromVisitId)?.customer.displayName ?? 'their last job';
    return `${who} en route · left ${from} at ${timeInTz(drawn.place.leftAt, tz)} · ${NO_GPS}`;
  }
  const first = jobOf(heading[0]!.toVisitId, byId);
  if (first && first.visit.id !== job.visit.id) {
    return `${who} marked on the way · heading to ${first.customer.displayName} first · ${NO_GPS}`;
  }
  const why = heading.find((t) => t.why)?.why;
  return `${who} en route${why ? ` · ${why}` : ''} · ${NO_GPS}`;
}

/* ───────────────────────── A picked tech ───────────────────────── */

/**
 * What a picked tech is doing now, in a sentence or two:
 * - on a job: "Beaumont Residence since 9:15 AM";
 * - en route: "Left A at T, heading to B. No GPS: the map draws them halfway along the line.";
 * - between jobs: the check-out, any skipped visit "still booked", and what is next;
 * - not started, done: their state, and the first or last of their day.
 */
export function techNow(
  tech: CrewTechSource,
  byId: ReadonlyMap<string, PanelJob>,
  tz: string,
  date: string,
): string[] {
  const jobs = mine(tech, byId);
  const name = (j: PanelJob | null) => j?.customer.displayName ?? 'a job';
  const skipped = new Set(tech.skipped);
  const state: CrewState = tech.state;
  switch (state) {
    case 'on_job': {
      const at = jobOf(tech.atVisitId, byId);
      const since =
        at?.visit.actualStartAt == null
          ? ''
          : ` since ${timeInTz(at.visit.actualStartAt, tz)}`;
      const lines = [`${name(at)}${since}`];
      if (tech.place === null && tech.why) lines.push(notOnTheMap(tech.why));
      return lines;
    }
    case 'en_route': {
      const to = jobOf(tech.toVisitId, byId);
      const also = jobs.filter((j) => j.visit.status === 'en_route' && j !== to);
      const alsoLine =
        also.length > 0
          ? [
              `Also marked on the way to ${listOf(also.map((j) => j.customer.displayName))}.`,
            ]
          : [];
      if (tech.place?.kind === 'between') {
        const from = byId.get(tech.place.fromVisitId) ?? null;
        return [
          `Left ${name(from)} at ${timeInTz(tech.place.leftAt, tz)}, heading to ${name(to)}. ${NO_GPS}: the map draws them halfway along the line.`,
          ...alsoLine,
        ];
      }
      return [
        `Heading to ${name(to)}.${tech.why ? ` ${tech.why}.` : ''} ${NO_GPS}, so the map doesn't draw them.`,
        ...alsoLine,
      ];
    }
    case 'between_jobs': {
      const left = lastLeft(tech, byId);
      const lines: string[] = [];
      if (left?.visit.actualEndAt != null) {
        lines.push(
          `Checked out of ${name(left)} at ${timeInTz(left.visit.actualEndAt, tz)}.`,
        );
      } else {
        lines.push('Between jobs.');
      }
      for (const id of tech.skipped) {
        const j = byId.get(id);
        if (!j) continue;
        lines.push(
          `${name(j)}, booked ${bookedAt(j.visit.scheduledStartAt, tz, date)}, is still booked.`,
        );
      }
      // Their day is in booked order: the first still to start that they did not pass over.
      const next = jobs.find(
        (j) => j.visit.status === 'scheduled' && !skipped.has(j.visit.id),
      );
      if (next) {
        lines.push(
          `Next: ${name(next)} at ${bookedAt(next.visit.scheduledStartAt, tz, date)}.`,
        );
      }
      return lines;
    }
    case 'not_started': {
      const first = jobs.find((j) => j.visit.status === 'scheduled');
      return first
        ? [
            `Not started. First booked: ${name(first)} at ${bookedAt(first.visit.scheduledStartAt, tz, date)}.`,
          ]
        : ['Not started.'];
    }
    default: {
      const left = lastLeft(tech, byId);
      if (jobs.length === 0) return ['Done for the day. Nothing booked today.'];
      return left?.visit.actualEndAt != null
        ? [
            `Done for the day. Last check-out ${timeInTz(left.visit.actualEndAt, tz)}, at ${name(left)}.`,
          ]
        : ['Done for the day.'];
    }
  }
}

/** The server's reason, said once: its `why` may already open with "Not on the map". */
const notOnTheMap = (why: string) =>
  /^not on the map/i.test(why) ? `${why}.` : `Not on the map: ${why}.`;

/** One stop of a picked tech's day. */
export interface TimelineStop {
  visitId: string;
  /** Their day's number: the one their pin carries while they are picked. Canceled: none. */
  order: number | null;
  customer: string;
  title: string;
  status: string;
  /** "In 8:07 AM · out 9:02 AM (55 min)", "Booked 1:00 PM", "Booked 9:00 AM · still booked". */
  line: string;
}

/** A picked tech's day, in its order, each stop with what happened at it. */
export function techTimeline(
  tech: CrewTechSource,
  byId: ReadonlyMap<string, PanelJob>,
  tz: string,
  date: string,
): TimelineStop[] {
  const order = dayOrder(tech, canceledVisits([...byId.values()]));
  const skipped = new Set(tech.skipped);
  return mine(tech, byId).map((j) => {
    const v = j.visit;
    const booked = `Booked ${bookedAt(v.scheduledStartAt, tz, date)}`;
    let line: string;
    switch (v.status) {
      case 'completed':
        line =
          v.actualStartAt !== null && v.actualEndAt !== null
            ? `In ${timeInTz(v.actualStartAt, tz)} · out ${timeInTz(v.actualEndAt, tz)} (${durationLabel(v.actualStartAt, v.actualEndAt)})`
            : v.actualEndAt !== null
              ? `Out ${timeInTz(v.actualEndAt, tz)}`
              : 'Done';
        break;
      case 'in_progress':
        line =
          v.actualStartAt === null
            ? 'On the job now'
            : `In ${timeInTz(v.actualStartAt, tz)} · on the job now`;
        break;
      case 'en_route':
        line = `${booked} · en route`;
        break;
      case 'canceled':
        line = `Canceled · was booked ${bookedAt(v.scheduledStartAt, tz, date)}`;
        break;
      default:
        line = skipped.has(v.id) ? `${booked} · still booked, not started` : booked;
    }
    return {
      visitId: v.id,
      order: order.get(v.id) ?? null,
      customer: j.customer.displayName,
      title: j.job.title,
      status: v.status,
      line,
    };
  });
}

/** The footnote under the panel: check-in and check-out are visit-level (CONTEXT.md). */
export const SHARED_JOB_NOTE =
  'On a shared job, everyone on it is checked in and out together.';
